import { ErrorNotice } from '@/components/ui/Feedback'
import { pageClassName } from '@/components/ui/Page'
import { ArrowLeft, Disc3, Music, Music2, Sparkles } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

import type { AlbumSummary, SearchResult } from '@/api/types'
import { Cover } from '@/components/Cover'
import { Button, CategoryTabs, EmptyState, TrackListSkeleton } from '@/components/ui'
import { AlbumGrid, AlbumGridSkeleton } from '@/features/search/AlbumGrid'
import { describeError } from '@/features/search/describeError'

import { ArtistSongs } from './ArtistSongs'
import { albumHref, providerArtistHref, similarHref } from './artistHref'
import { useArtist, useArtistContent } from './hooks'

export function ArtistPage() {
  const { artistId, localArtistId, provider, externalId } = useParams()
  const path = localArtistId ? `/artists/local/${encodeURIComponent(localArtistId)}` : provider && externalId
    ? providerArtistHref(provider, externalId)
    : `/artists/${encodeURIComponent(artistId ?? '')}`
  return <ArtistProfileView key={path} path={path} />
}

function ArtistProfileView({ path }: { path: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const profile = useArtist(path)
  const [params, setParams] = useSearchParams()
  const section = params.get('view') === 'albums' ? 'albums' : 'songs'
  const setSection = (value: 'songs' | 'albums') => {
    const next = new URLSearchParams(params)
    next.set('view', value)
    setParams(next)
  }
  const songs = useArtistContent<SearchResult>(path, 'songs', !!profile.data && section === 'songs')
  const albums = useArtistContent<AlbumSummary>(path, 'albums', !!profile.data && section === 'albums')
  const artist = profile.data
  const active = section === 'songs' ? songs : albums
  // The same track may appear on several releases. Keep distinct recordings,
  // but do not show the same provider track twice as more releases are loaded.
  const tracks = [...new Map((songs.data?.pages.flatMap((page) => page.results) ?? [])
    .map((track) => [`${track.provider}:${track.external_id}`, track])).values()]
  const releases = [...new Map((albums.data?.pages.flatMap((page) => page.results) ?? [])
    .map((album) => [`${album.provider}:${album.external_id}`, album])).values()]

  return (
    <div className={pageClassName('collection')}>
      <Button variant="ghost" className="-ml-3 mb-4" onClick={() => {
        if (location.key === 'default') void navigate('/')
        else void navigate(-1)
      }}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
      </Button>

      {profile.isPending ? <TrackListSkeleton count={8} /> : profile.isError ? (
        <ErrorNotice title="Could not load artist" onRetry={() => void profile.refetch()} busy={profile.isFetching}>{describeError(profile.error, 'Please try again.')}</ErrorNotice>
      ) : artist ? (
        <>
          <header className="mb-5 flex items-center gap-4 rounded-card border border-border bg-gradient-to-br from-accent-soft to-surface-raised p-4 text-left sm:items-end sm:p-6">
            <Cover src={artist.picture_url} size="xl" className="w-20 shrink-0 rounded-full shadow-lg sm:w-44" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">Artist</p>
              <h1 className="mt-1 break-words text-2xl font-bold tracking-tight text-text sm:text-4xl">{artist.name}</h1>
              <p className="mt-2 text-xs text-text-muted sm:text-sm">
                {artist.popularity != null ? `${artist.popularity.toLocaleString()} fans · ` : ''}
                {artist.library_count} {artist.library_count === 1 ? 'song' : 'songs'} in your library
              </p>
              {artist.can_recommend ? (
                <Link to={`${artist.local_artist_id ? similarHref(artist) : `/similar/provider/${encodeURIComponent(artist.provider)}/${encodeURIComponent(artist.external_id)}`}?name=${encodeURIComponent(artist.name)}`}
                  className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-lg border border-border bg-surface-raised px-4 text-sm font-medium hover:bg-surface-hover">
                  <Sparkles className="h-4 w-4" aria-hidden="true" /> More like this
                </Link>
              ) : null}
            </div>
          </header>

          {artist.catalog_available ? (
            <CategoryTabs className="mb-4" label="Artist sections" value={section} onChange={setSection}
              tabId={(value) => `artist-tab-${value}`} panelId="artist-content"
              options={[{ value: 'songs', label: 'Songs', icon: Music2 }, { value: 'albums', label: 'Albums & singles', icon: Disc3 }]} />
          ) : (
            <p className="mb-5 text-sm text-text-muted">Songs from your library. This music source does not offer a full artist catalog.</p>
          )}

          <section id="artist-content" role={artist.catalog_available ? "tabpanel" : undefined} aria-labelledby={artist.catalog_available ? `artist-tab-${section}` : undefined} aria-label={section === 'songs' ? 'Songs' : 'Albums and singles'}>
            <h2 className="sr-only">{section === 'songs' ? 'Songs' : 'Albums & singles'}</h2>
            {active.isPending ? (
              section === 'songs' ? <TrackListSkeleton count={8} /> : <AlbumGridSkeleton />
            ) : (
              <>
                {section === 'songs' ? <ArtistSongs tracks={tracks} /> :
                  <AlbumGrid albums={releases} providers={[]} onSelect={(album) => void navigate(albumHref(album))} />}
                {!active.isError && !active.hasNextPage && (section === 'songs' ? tracks.length === 0 : releases.length === 0) ? (
                  <EmptyState icon={Music} title={section === 'songs' ? 'No songs available' : 'No releases available'}
                    description={artist.catalog_available ? 'This music source has no music to show here yet.' : 'Songs you add by this artist will appear here.'} />
                ) : null}
              </>
            )}
            {active.isError ? (
              <ErrorNotice onRetry={() => void (active.isFetchNextPageError ? active.fetchNextPage() : active.refetch())} busy={active.isFetching}>{describeError(active.error, 'Could not load more music.')}</ErrorNotice>
            ) : null}
            {active.hasNextPage && !active.isError ? (
              <div className="flex justify-center py-6">
                <Button variant="secondary" loading={active.isFetching} onClick={() => void active.fetchNextPage()}>
                  {section === 'songs' ? 'Load more songs' : 'Load more releases'}
                </Button>
              </div>
            ) : null}
          </section>

        </>
      ) : null}
    </div>
  )
}
